import {
	Card,
	Button,
	ListGroup,
	Alert,
	Spinner,
	Modal,
	Badge,
	Col,
	Row,
} from 'react-bootstrap';
import { FaDownload, FaTrash } from 'react-icons/fa';
import WordIcon from '../../../icons/word_icon.svg';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import PdfIcon from '../../../icons/pdf_icon.svg';
import SpinnerButton from '../../../components/SpinnerButton/SpinnerButton';
import { useMemo, useState } from 'react';
import Btn from 'src/components/Btn/Btn';
import { formatDate } from 'src/utils';
import { RoleEnum } from 'src/api';
import { useAuth } from 'src/hooks/useAuth';

const OpportunityAttachments = ({
	opportunityResponse,
	updating,
	setShowUploadDocsModal,
	downloadingAttachment,
	downloadAttachment,
	loadedDocuments = [],
	isLoading,
	removingAttachment,
	removeAttachment,
	previewAttachment,
	customTitle,
	isLegacy,
}: any) => {
	const { t } = useTranslation();
	const { userRole } = useAuth();
	const [showAttachmentRemovalModal, setAttachmentRemovalModal] =
		useState(false);
	const [selectedAttachment, setSelectedAttachment] = useState<any>(null);
	const isSegnalazione = opportunityResponse?.Canale__c?.toLowerCase() === 'segnalatore';
	const isSalesDealer = userRole === RoleEnum.SALES_DEALER;
	const isOpportunityReadOnly = isSalesDealer && isSegnalazione;

	const attachments = useMemo(() => {
		const loadedTitles = (loadedDocuments || []).map(
			(doc) => doc.uploadedFileName
		);
		return (opportunityResponse.attachments || []).filter(
			(attachment) => !loadedTitles.includes(attachment.Title)
		);
	}, [opportunityResponse, loadedDocuments]);

	const onRemoveAttachment = async (attachment) => {
		await removeAttachment(attachment);
		setSelectedAttachment(null);
		setAttachmentRemovalModal(false);
	};

	const canViewUploadDate = [
		RoleEnum.ADMIN,
		RoleEnum.BACKOFFICE,
		RoleEnum.MASTER,
		RoleEnum.SALES,
		RoleEnum.SALES_DEALER,
		RoleEnum.SALES_INTERNAL,
		RoleEnum.AREA_MANAGER,
	].includes(userRole);

	return (
		<>
			<div className="d-flex justify-content-between mt-4">
				<Card.Title className="fw-bold fs-5 text-black">
					{customTitle || t('uploaded_documents')}
				</Card.Title>
				{setShowUploadDocsModal ? (
					<Btn onClick={() => setShowUploadDocsModal(true)} disabled={updating || isOpportunityReadOnly}>
						<SpinnerButton
							label={i18next.t('upload_other_docs')}
							busy={false}
						/>
					</Btn>
				) : null}
			</div>

			{attachments && attachments.length > 0 && !isLoading ? (
				<ListGroup variant="flush mt-2">
					{(attachments ?? []).map((el) => (
						<ListGroup.Item key={el.Id}>
							<Row className="justify-content-between align-items-center d-flex">
								<Col className="d-flex align-items-center">
									{el.Title.endsWith('doc') || el.Title.endsWith('docx') ? (
										<img
											width={35}
											src={WordIcon}
											className="file-icon-size word-icon"
											alt="Word document"
										/>
									) : (
										<img
											width={25}
											src={PdfIcon}
											className="file-icon-size pdf-icon"
											alt="PDF document"
										/>
									)}
									<div
										// style={{ width: '100%', cursor: 'pointer' }}
										className="px-3 text-decoration-underline text-truncate"
										onClick={() => previewAttachment(el)}
									>
										{el.Title}
									</div>
								</Col>

								<Col className="d-flex align-items-center justify-content-end">
									{canViewUploadDate && el.CreatedDate ? (
										<Badge bg="secondary" className="m-2">
											{formatDate(el.CreatedDate, 'it-IT', true)}
										</Badge>
									) : null}
									<Button
										disabled={downloadingAttachment === el.Id}
										variant="link"
										onClick={() => downloadAttachment(el)}
									>
										{downloadingAttachment === el.Id ? (
											<Spinner animation="border" size="sm" />
										) : (
											<FaDownload />
										)}
									</Button>
									<Button
										disabled={removingAttachment === el.Id || isOpportunityReadOnly}
										variant="link"
										onClick={() => {
											setSelectedAttachment(el);
											setAttachmentRemovalModal(true);
										}}
									>
										{removingAttachment === el.Id ? (
											<Spinner animation="border" size="sm" />
										) : (
											<FaTrash />
										)}
									</Button>
								</Col>
							</Row>
						</ListGroup.Item>
					))}
				</ListGroup>
			) : (
				<Alert className="mt-4">{t('no_uploaded_document')}</Alert>
			)}
			<Modal
				show={showAttachmentRemovalModal}
				onExit={() => setAttachmentRemovalModal(false)}
			>
				<Modal.Header>
					<Modal.Title>{t('remove_opportunity_attachment_title')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					{t('remove_opportunity_attachment_request', {
						name: selectedAttachment?.Title,
					})}
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="link"
						onClick={() => setAttachmentRemovalModal(false)}
						disabled={updating}
					>
						{t('cancel')}
					</Button>
					<Button
						onClick={() => onRemoveAttachment(selectedAttachment)}
						disabled={removingAttachment}
					>
						{removingAttachment ? (
							<Spinner animation="border" size="sm" />
						) : (
							i18next.t('confirm')
						)}
					</Button>
				</Modal.Footer>
			</Modal>
		</>
	);
};

export default OpportunityAttachments;
